import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { categoryService } from '../../../services/categoryService';
import { locationService } from '../../../services/locationService';
import { routeService } from '../../../services/routeService';
import './AdminCategories.css';

// Функции для конвертации типа
const convertTypeToDisplay = (type) => {
  // type из БД: 'Локации' или 'Маршруты' (русский)
  if (type === 'Локации') return 'Локации';
  if (type === 'Маршруты') return 'Маршруты';
  // fallback для английских значений
  if (type === 'location') return 'Локации';
  if (type === 'route') return 'Маршруты';
  return type;
};

const convertTypeToApi = (displayType) => {
  // displayType: 'Локации' или 'Маршруты'
  if (displayType === 'Локации') return 'location';
  if (displayType === 'Маршруты') return 'route';
  return displayType;
};

const AdminCategoryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Локации',
    assignedItems: []
  });
  
  const [availableItems, setAvailableItems] = useState({
    locations: [],
    routes: []
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showTypeWarning, setShowTypeWarning] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState(null);
  const [assignedIds, setAssignedIds] = useState(new Set());

  useEffect(() => {
    setAssignedIds(new Set(formData.assignedItems.map(item => item.id)));
  }, [formData.assignedItems]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Загружаем данные категории, все локации и маршруты
      const [category, locations, routes] = await Promise.all([
        categoryService.getCategoryById(id),
        locationService.getAllLocations(),
        routeService.getAllRoutes()
      ]);
      
      console.log('Загруженная категория (raw):', category);
      console.log('Тип из БД:', category.type);
      
      // Конвертируем тип для отображения
      const displayType = convertTypeToDisplay(category.type);
      console.log('Тип для отображения:', displayType);
      
      // Загружаем элементы, принадлежащие этой категории
      let assignedItems = [];
      if (category.type === 'Локации' || category.type === 'location') {
        const locationsForCategory = await categoryService.getCategoryLocations(id);
        assignedItems = Array.isArray(locationsForCategory) ? locationsForCategory : [];
        console.log('Загружены локации для категории:', assignedItems.length);
      } else if (category.type === 'Маршруты' || category.type === 'route') {
        const routesForCategory = await categoryService.getCategoryRoutes(id);
        assignedItems = Array.isArray(routesForCategory) ? routesForCategory : [];
        console.log('Загружены маршруты для категории:', assignedItems.length);
      }
      
      setFormData({
        name: category.name,
        description: category.description || '',
        type: displayType,
        assignedItems: assignedItems
      });
      
      setAvailableItems({
        locations: Array.isArray(locations) ? locations : [],
        routes: Array.isArray(routes) ? routes : []
      });
      
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setError(error.message || 'Не удалось загрузить данные категории');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTypeChange = (newType) => {
    if (newType !== formData.type && formData.assignedItems.length > 0) {
      setPendingTypeChange(newType);
      setShowTypeWarning(true);
    } 
    else if (newType !== formData.type) {
      setFormData(prev => ({
        ...prev,
        type: newType,
        assignedItems: []
      }));
    }
  };

  const confirmTypeChange = () => {
    setFormData(prev => ({
      ...prev,
      type: pendingTypeChange,
      assignedItems: []
    }));
    setShowTypeWarning(false);
    setPendingTypeChange(null);
  };

  const cancelTypeChange = () => {
    setShowTypeWarning(false);
    setPendingTypeChange(null);
  };

  const handleAddItem = (item) => {
    setFormData(prev => ({
      ...prev,
      assignedItems: [...prev.assignedItems, item]
    }));
  };

  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      assignedItems: prev.assignedItems.filter(item => item.id !== itemId)
    }));
  };

  const getUnassignedItems = () => {
    const items = formData.type === 'Локации' 
      ? (availableItems.locations || [])
      : (availableItems.routes || []);
    
    return items.filter(item => !assignedIds.has(item.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Пожалуйста, введите название категории');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Конвертируем тип для API
      const apiType = convertTypeToApi(formData.type);
      
      console.log('1. Обновляем категорию:', {
        id,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        type: formData.type,      // для отображения
        apiType: apiType          // для API
      });
      
      // 1. Обновляем основную информацию категории
      const updatedCategory = await categoryService.updateCategory(
        id,
        formData.name.trim(),
        formData.description.trim() || null,
        apiType
      );
      
      console.log('2. Категория обновлена:', updatedCategory);
      
      // 2. Обновляем связи
      const itemIds = formData.assignedItems.map(item => item.id);
      console.log(`3. Обновляем связи для категории ${id}, элементы:`, itemIds);
      
      if (formData.type === 'Локации') {
        await categoryService.updateCategoryLocations(id, itemIds);
      } else {
        await categoryService.updateCategoryRoutes(id, itemIds);
      }
      
      console.log('4. Связи обновлены успешно');
      
      alert('Категория успешно обновлена');
      navigate('/admin/categories/list');
      
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setError(error.message || 'Не удалось обновить категорию');
    } finally {
      setSubmitting(false);
    }
  };

  const currentItems = formData.assignedItems || [];
  const unassignedItems = getUnassignedItems();

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Загрузка данных категории...</p>
      </div>
    );
  }

  return (
    <div className="admin-category-form">
      <h2 className="admin-category-form__title">Редактирование категории</h2>

      {error && (
        <div className="admin-category-form__error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {showTypeWarning && (
        <div className="admin-category-form__warning">
          <div className="admin-category-form__warning-content">
            <h4>Внимание!</h4>
            <p>При изменении типа категории все выбранные элементы будут удалены. Продолжить?</p>
            <div className="admin-category-form__warning-actions">
              <button 
                type="button" 
                onClick={confirmTypeChange}
                className="warning-confirm"
              >
                Да, изменить тип
              </button>
              <button 
                type="button" 
                onClick={cancelTypeChange}
                className="warning-cancel"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-category-form__form">
        {/* Основная информация */}
        <div className="admin-category-form__section">
          <h3 className="admin-category-form__section-title">Основная информация</h3>
          
          <div className="admin-category-form__field">
            <label htmlFor="name">
              Название категории <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={submitting}
              placeholder="Введите название категории"
              autoFocus
            />
          </div>

          <div className="admin-category-form__field">
            <label htmlFor="description">Описание</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              disabled={submitting}
              placeholder="Введите описание категории"
            />
          </div>

          <div className="admin-category-form__field">
            <label>Тип категории <span className="required">*</span></label>
            <div className="admin-category-form__type-selector">
              <label className={`type-option ${formData.type === 'Локации' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="type"
                  value="Локации"
                  checked={formData.type === 'Локации'}
                  onChange={() => handleTypeChange('Локации')}
                  disabled={submitting}
                />
                <span className="type-icon">📍</span>
                <span className="type-label">Локации</span>
                {formData.type === 'Локации' && (
                  <span className="type-current">текущий</span>
                )}
              </label>
              <label className={`type-option ${formData.type === 'Маршруты' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="type"
                  value="Маршруты"
                  checked={formData.type === 'Маршруты'}
                  onChange={() => handleTypeChange('Маршруты')}
                  disabled={submitting}
                />
                <span className="type-icon">🗺️</span>
                <span className="type-label">Маршруты</span>
                {formData.type === 'Маршруты' && (
                  <span className="type-current">текущий</span>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Управление элементами */}
        <div className="admin-category-form__section">
          <h3 className="admin-category-form__section-title">
            Управление {formData.type === 'Локации' ? 'локациями' : 'маршрутами'}
          </h3>
          
          <div className="admin-category-form__items-management">
            {/* Левый список: элементы в категории */}
            <div className="items-list items-list--assigned">
              <div className="items-list__header">
                <h4>
                  {formData.type === 'Локации' ? 'Локации' : 'Маршруты'} в категории
                  <span className="items-count">({currentItems.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {currentItems.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">📭</span>
                    <p>Нет добавленных элементов</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {currentItems.map(item => (
                      <div key={item.id} className="item-card">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="item-card__remove"
                          disabled={submitting}
                          title="Удалить из категории"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Правый список: доступные элементы */}
            <div className="items-list items-list--available">
              <div className="items-list__header">
                <h4>
                  Доступные {formData.type === 'Локации' ? 'локации' : 'маршруты'}
                  <span className="items-count">({unassignedItems.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {unassignedItems.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">✅</span>
                    <p>Все элементы уже добавлены</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {unassignedItems.map(item => (
                      <div key={item.id} className="item-card item-card--available">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddItem(item)}
                          className="item-card__add"
                          disabled={submitting}
                          title="Добавить в категорию"
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="admin-category-form__actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || !formData.name.trim()}
          >
            {submitting ? (
              <>
                <span className="btn-spinner"></span>
                Сохранение...
              </>
            ) : (
              'Сохранить изменения'
            )}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/admin/categories/list')} 
            className="btn btn-secondary"
            disabled={submitting}
          >
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCategoryEdit;